import React, { useState, useEffect, useRef } from 'react';
import Hero from './components/Hero';
import About from './components/About';
import AgentDirectory from './components/AgentDirectory';
import AgentModal from './components/AgentModal';
import AboutUs from './components/AboutUs';
import Navigation from './components/Navigation';
import { ExternalLink } from 'lucide-react';

import AdminDashboard from './components/admin/AdminDashboard';
import { Agent } from './types/Agent';
import { useAccessIdentity } from './hooks/useAccessIdentity';

const defaultSocialLinks = [
  { id: '1', title: 'Facebook', url: 'https://facebook.com/bbhainsurance' },
  { id: '2', title: 'LinkedIn', url: 'https://linkedin.com/company/bbhainsurance' }
];

const App: React.FC = () => {
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBioAgent, setSelectedBioAgent] = useState<Agent | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const currentPath = window.location.pathname;
  const { user, loading, error: authError } = useAccessIdentity(currentPath === '/admin');
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const [socialLinks, setSocialLinks] = useState<Array<{id: string, title: string, url: string}>>([]);

  // Get social media icon
  const getSocialIcon = (title: string) => {
    const iconClass = "w-5 h-5";
    switch (title.toLowerCase()) {
      case 'facebook':
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
          </svg>
        );
      case 'linkedin':
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        );
      case 'twitter':
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"/>
          </svg>
        );
      case 'instagram':
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path fillRule="evenodd" d="M12.017 0C8.396 0 7.932.013 6.714.06 5.499.107 4.677.278 3.967.528a5.906 5.906 0 00-2.141 1.384A5.909 5.909 0 00.528 3.967c-.25.71-.421 1.532-.468 2.747C.013 7.932 0 8.396 0 12.017c0 3.624.013 4.09.06 5.303.047 1.215.218 2.037.468 2.747a5.909 5.909 0 001.384 2.141A5.906 5.906 0 003.967 23.472c.71.25 1.532.421 2.747.468 1.218.047 1.682.06 5.303.06 3.624 0 4.09-.013 5.303-.06 1.215-.047 2.037-.218 2.747-.468a5.906 5.906 0 002.141-1.384 5.909 5.909 0 001.384-2.141c.25-.71.421-1.532.468-2.747.047-1.218.06-1.682.06-5.303 0-3.624-.013-4.09-.06-5.303-.047-1.215-.218-2.037-.468-2.747a5.909 5.909 0 00-1.384-2.141A5.906 5.906 0 0020.033.528c-.71-.25-1.532-.421-2.747-.468C16.068.013 15.604 0 12.017 0zm-.017 2.17c3.556 0 3.987.01 5.386.056.94.042 1.45.194 1.789.323.45.175.772.384 1.11.722.339.338.547.66.722 1.11.129.339.281.849.323 1.789.046 1.399.056 1.83.056 5.386 0 3.556-.01 3.987-.056 5.386-.042.94-.194 1.45-.323 1.789a2.99 2.99 0 01-.722 1.11c-.338.339-.66.547-1.11.722-.339.129-.849.281-1.789.323-1.399.046-1.83.056-5.386.056-3.556 0-3.987-.01-5.386-.056-.94-.042-1.45-.194-1.789-.323a2.99 2.99 0 01-1.11-.722 2.99 2.99 0 01-.722-1.11c-.129-.339-.281-.849-.323-1.789-.046-1.399-.056-1.83-.056-5.386 0-3.556.01-3.987.056-5.386.042-.94.194-1.45.323-1.789.175-.45.384-.772.722-1.11.338-.339.66-.547 1.11-.722.339-.129.849-.281 1.789-.323 1.399-.046 1.83-.056 5.386-.056z" clipRule="evenodd" />
            <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
          </svg>
        );
      default:
        return <ExternalLink size={20} />;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Set new timeout to detect when scrolling stops
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Load social links
  useEffect(() => {
    const loadSocialLinks = async () => {
      try {
        // Try to fetch from admin data first, fallback to default links
        const response = await fetch('/data/companyLinks.json').catch(() => null);
        if (response?.ok && response.headers.get('content-type')?.includes('application/json')) {
          const data = await response.json();
          if (data.social) {
            setSocialLinks(data.social);
          } else {
            setSocialLinks(defaultSocialLinks);
          }
        } else {
          setSocialLinks(defaultSocialLinks);
        }
      } catch {
        setSocialLinks(defaultSocialLinks);
      }
    };
    loadSocialLinks();
  }, []);

  const handleAgentClick = (agent: Agent) => {
    setSelectedAgent(agent);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAgent(null);
  };

  const handleBioClick = (agent: Agent) => {
    setSelectedBioAgent(agent);
  };

  const closeBioDialog = () => {
    setSelectedBioAgent(null);
  };

  const handleLearnMore = (agent: Agent) => {
    // Smooth transition: delay bio dialog open slightly after modal closes
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedBioAgent(agent);
    }, 150);
  };

  // Admin logout handler
  const handleAdminLogout = () => {
    window.location.href = '/cdn-cgi/access/logout';
  };

  // Smooth scrolling to sections
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 80; // Account for fixed navigation
      const elementPosition = element.offsetTop - navHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  // Admin route rendering
  if (currentPath === '/admin') {
    if (loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-bbha-plum-600 to-bbha-plum-800 flex items-center justify-center">
          <div className="text-white text-xl">Loading...</div>
        </div>
      );
    }
    
    if (!user) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-bbha-plum-600 to-bbha-plum-800 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 w-full max-w-md border border-white/20 text-center">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">We couldn’t open the editor</h2>
            <p className="text-gray-600 mb-6">
              {authError || 'Please sign in again to continue.'}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              If you run into any issues, contact Kyle at{' '}
              <a className="underline" href="mailto:kylemirich20@gmail.com">kylemirich20@gmail.com</a>.
            </p>
            <button
              onClick={handleAdminLogout}
              className="w-full bg-bbha-olive-600 text-white py-2 px-4 rounded-lg hover:bg-bbha-olive-700 transition-colors"
            >
              Return to site
            </button>
          </div>
        </div>
      );
    }
    
    return <AdminDashboard user={user} onLogout={handleAdminLogout} />;
  }


  // Main site rendering
  return (
    <div className="min-h-screen bg-white relative">
      {/* Futuristic scroll indicator */}
      <div 
        className={`fixed top-0 left-0 h-1 bg-gradient-to-r from-bbha-olive-500 via-bbha-plum-500 to-bbha-olive-500 z-50 transition-all duration-300 ${isScrolling ? 'opacity-100' : 'opacity-0'}`}
        style={{ 
          width: `${Math.min((scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100, 100)}%`,
          boxShadow: '0 0 10px rgba(74, 44, 77, 0.5)'
        }}
      />
      
      <Navigation scrollToSection={scrollToSection} />
      
      <Hero scrollToSection={scrollToSection} />
      
      <main role="main" id="main-content">
        <section id="agents" aria-labelledby="agents-heading" className="scroll-mt-20">
          <AgentDirectory onAgentClick={handleAgentClick} onBioClick={handleBioClick} />
        </section>
        <section id="about" aria-labelledby="about-heading" className="scroll-mt-20">
          <About />
        </section>
        <section id="contact" aria-labelledby="contact-heading" className="scroll-mt-20">
          <AboutUs />
        </section>
      </main>
      
      <footer role="contentinfo" className="bg-gray-50 border-t">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="text-center text-gray-600">
            <p className="text-sm mb-4">
              BBHA Insurance Services - Independent Licensed Medicare Agents serving California
            </p>
            {/* Social Links */}
            {socialLinks.length > 0 && (
              <div className="flex justify-center gap-6 mb-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-bbha-plum-600 transition-colors flex items-center gap-2 text-sm font-medium"
                    aria-label={`Follow us on ${link.title}`}
                  >
                    {getSocialIcon(link.title)}
                    {link.title}
                  </a>
                ))}
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-4">
              Medicare has neither reviewed nor endorsed this information. We do not offer every plan available in your area. 
              Currently we represent organizations that offer products in your area. Please contact Medicare.gov or 1-800-MEDICARE to get information on all your options.
            </p>
          </div>
        </div>
      </footer>

      {isModalOpen && selectedAgent && (
        <AgentModal agent={selectedAgent} onClose={closeModal} onLearnMore={handleLearnMore} />
      )}

      {selectedBioAgent && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-40 p-6 py-12 animate-in fade-in duration-200"
          onClick={closeBioDialog}
        >
          <div 
            className="bg-white/95 backdrop-blur-sm rounded-xl max-w-lg w-full max-h-[70vh] shadow-2xl border border-white/20 relative flex flex-col animate-in slide-in-from-bottom-4 zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-4">
                  <img
                    src={selectedBioAgent.image}
                    alt={selectedBioAgent.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{selectedBioAgent.name}</h3>
                    <p className="text-bbha-plum-600 font-semibold text-lg">{selectedBioAgent.title}</p>
                  </div>
                </div>
                <button
                  onClick={closeBioDialog}
                  aria-label="Close biography"
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">About {selectedBioAgent.name}</h4>
                <p className="text-gray-600 text-lg leading-relaxed whitespace-pre-line">
                  {selectedBioAgent.bio}
                </p>
              </div>
              
              {selectedBioAgent.specialties && selectedBioAgent.specialties.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Specialties</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedBioAgent.specialties.map((specialty, index) => (
                      <span
                        key={index}
                        className="bg-bbha-plum-100 text-bbha-plum-800 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedBioAgent.yearsExperience > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Experience</h4>
                  <p className="text-gray-600">{selectedBioAgent.yearsExperience} years in Medicare services</p>
                </div>
              )}
            </div>
            
            {/* Fixed Bottom Bar - Only within this dialog */}
            <div className="border-t border-gray-200 p-4 bg-white rounded-b-xl flex-shrink-0">
              {selectedBioAgent.websiteUrl && /^https?:\/\//.test(selectedBioAgent.websiteUrl) && (
                <a href={selectedBioAgent.websiteUrl} target="_blank" rel="noopener noreferrer"
                  className="block text-center text-bbha-plum-700 font-semibold mb-3 underline">
                  Visit {selectedBioAgent.name.split(' ')[0]}'s Website
                </a>
              )}
              <button
                onClick={() => {
                  closeBioDialog();
                  handleAgentClick(selectedBioAgent);
                }}
                className="w-full bg-gradient-to-r from-bbha-olive-600 to-bbha-olive-700 text-white py-3 rounded-lg font-semibold hover:from-bbha-olive-700 hover:to-bbha-olive-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-base lg:text-lg"
              >
                Click to Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
